import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import api from '../lib/api';
import { MapPin, Clock, Navigation, List, Map, CheckCircle2, XCircle, Circle, Loader2, Check, SkipForward, Flag, Target } from 'lucide-react';

// SVG icons for map markers
const createCheckCircleSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"/>
  <path d="m9 12 2 2 4-4"/>
</svg>`;

const createXCircleSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"/>
  <path d="m15 9-6 6"/>
  <path d="m9 9 6 6"/>
</svg>`;

const createCheckSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
  <path d="M20 6 9 17l-5-5"/>
</svg>`;

const createSkipForwardSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <polygon points="5 4 15 12 5 20 5 4"/>
  <line x1="19" x2="19" y1="5" y2="19"/>
</svg>`;

// Target/crosshair SVG for start location
const createTargetSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"/>
  <circle cx="12" cy="12" r="6"/>
  <circle cx="12" cy="12" r="2"/>
</svg>`;

// Flag SVG for end location
const createFlagSvg = (color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
  <line x1="4" x2="4" y1="22" y2="15"/>
</svg>`;

// Custom marker icons - colored TEXT numbers matching Route Map style
const createNumberedIcon = (number, status) => {
  // Determine color based on status - matching Route Map colors
  let color = '#ffffff'; // Default white for pending
  if (status === 'successful') color = '#22c55e'; // Green for served
  else if (status === 'unsuccessful') color = '#ef4444'; // Red for failed
  else if (status === 'completed') color = '#a855f7'; // Purple for completed
  else if (status === 'skipped') color = '#6b7280'; // Gray for skipped
  
  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `<div style="color: ${color}; font-weight: bold; font-size: 14px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">${number}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Start icon - Green "S" text matching Route Map style
const startIcon = L.divIcon({
  className: 'custom-start-end-marker',
  html: `<div style="color: #4ade80; font-weight: bold; font-size: 15px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">S</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// End icon - Red "E" text matching Route Map style
const endIcon = L.divIcon({
  className: 'custom-start-end-marker',
  html: `<div style="color: #f87171; font-weight: bold; font-size: 15px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">E</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Helper to convert 24H to 12H format
const formatTime12h = (time24) => {
  if (!time24 || time24 === '-') return '-';
  try {
    const [hour, minute] = time24.split(':').map(Number);
    const period = hour < 12 ? 'am' : 'pm';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')}${period}`;
  } catch {
    return time24;
  }
};

// Helper to format date as MM-DD-YYYY
const formatDateUS = (dateStr) => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

export default function TrackRoute() {
  const { shareToken } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'map'
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRoute = useCallback(async () => {
    try {
      const response = await api.getSharedRoute(shareToken);
      setRoute(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Route not found or link expired');
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    fetchRoute();
    
    // Auto-refresh every 10 seconds for more responsive updates
    const interval = setInterval(fetchRoute, 10000);
    return () => clearInterval(interval);
  }, [fetchRoute]);

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-2" />
          <p className="text-slate-600">Loading route...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Route Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const mapBounds = route?.stops?.filter(s => s.lat && s.lng).map(s => [s.lat, s.lng]) || [];
  if (route?.start_lat && route?.start_lng) {
    mapBounds.push([route.start_lat, route.start_lng]);
  }

  return (
    <div className="min-h-screen bg-slate-100 relative overflow-hidden">
      {/* Diagonal Servio Logo Watermark - spans across content */}
      <div className="fixed inset-0 pointer-events-none z-10 overflow-hidden flex items-center justify-center">
        <img 
          src="/servio-logo-optimized.png" 
          alt="" 
          className="select-none"
          style={{ 
            width: '500px',
            height: 'auto',
            transform: 'rotate(-35deg)',
            opacity: 0.12
          }}
        />
      </div>
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Profile picture or initials fallback */}
              {route.owner_photo ? (
                <img 
                  src={route.owner_photo} 
                  alt={route.owner_name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-teal-500"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold">
                  {route.owner_initials}
                </div>
              )}
              <div>
                <h1 className="font-semibold text-slate-800">{route.owner_name}</h1>
                <p className="text-sm text-slate-500">
                  {(() => {
                    const totalStops = route.stops?.length || 0;
                    const doneStops = route.stops?.filter(s => 
                      ['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status)
                    ).length || 0;
                    
                    if (route.status === 'completed' || doneStops === totalStops) {
                      return 'Route Completed';
                    } else if (route.status === 'in_progress' || doneStops > 0) {
                      return `${doneStops} of ${totalStops} stops completed`;
                    } else if (route.status === 'draft') {
                      return 'Not Started';
                    } else {
                      return route.status;
                    }
                  })()}
                </p>
                <p className="text-sm text-slate-500">
                  {route.remaining_distance?.toFixed(1) || route.total_distance?.toFixed(1)} mi remaining • {formatDuration(route.remaining_duration || route.total_duration)} left
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400">Auto-refreshes every 10 seconds</p>
              {lastUpdated && (
                <p className="text-[10px] text-slate-400">Updated {lastUpdated.toLocaleTimeString()}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Date and Route Name with Start/End Times */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <p className="text-sm text-slate-500">{formatDateUS(route.date)}</p>
          <h2 className="text-lg font-bold text-slate-800">{route.name}</h2>
          {(route.route_start_time || route.actual_start_time || route.estimated_end_time) && (
            <p className="text-sm text-teal-600 mt-1">
              {(route.actual_start_time || route.route_start_time) && 
                `Start: ${formatTime12h(route.actual_start_time || route.route_start_time)}`}
              {(route.actual_start_time || route.route_start_time) && route.estimated_end_time && ' • '}
              {route.estimated_end_time && `End: ${formatTime12h(route.estimated_end_time)}`}
            </p>
          )}
        </div>
      </div>

      {/* View Toggle */}
      <div className="sticky top-[72px] z-40 bg-slate-100 pb-2 pt-2">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex bg-white rounded-full p-1 shadow-sm border border-slate-200 w-fit">
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                view === 'list' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setView('map')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                view === 'map' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Map className="w-4 h-4" />
              Map
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        {view === 'list' ? (
          /* List View */
          <div className="space-y-2 mt-2">
            {/* Start location - Green "S" text style */}
            {route.start_address && (
              <div className="bg-white rounded-lg border border-green-200 bg-green-50/30 p-3">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 flex items-center justify-center text-sm font-bold text-green-400 flex-shrink-0" style={{textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'}}>
                    S
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-green-700">
                      {route.start_address}
                    </p>
                    <p className="text-xs text-green-500">Start location</p>
                  </div>
                </div>
              </div>
            )}
            
            {route.stops.map((stop, index) => {
              // Handle all status types: successful, unsuccessful, completed, skipped, pending
              const isServedSuccess = stop.status === 'successful';
              const isCompleted = stop.status === 'completed';
              const isFailed = stop.status === 'unsuccessful';
              const isSkipped = stop.status === 'skipped';
              const isDone = isServedSuccess || isCompleted || isFailed || isSkipped;
              
              // Determine colors based on status - matching Route Map text colors
              let borderClass = 'border-slate-200';
              let bgClass = '';
              let textClass = 'text-slate-800';
              let etaClass = 'text-slate-700';
              let statusLabel = null;
              let numberColor = 'text-white'; // Default white for pending
              
              if (isServedSuccess) {
                borderClass = 'border-green-200';
                bgClass = 'bg-green-50/50';
                textClass = 'text-green-700';
                etaClass = 'text-green-600';
                statusLabel = <span className="text-xs text-green-600">Served</span>;
                numberColor = 'text-green-400';
              } else if (isCompleted) {
                borderClass = 'border-purple-200';
                bgClass = 'bg-purple-50/50';
                textClass = 'text-purple-700';
                etaClass = 'text-purple-600';
                statusLabel = <span className="text-xs text-purple-600">Complete</span>;
                numberColor = 'text-purple-400';
              } else if (isFailed) {
                borderClass = 'border-red-200';
                bgClass = 'bg-red-50/50';
                textClass = 'text-red-700';
                etaClass = 'text-red-600';
                statusLabel = <span className="text-xs text-red-600">Failed</span>;
                numberColor = 'text-red-400';
              } else if (isSkipped) {
                borderClass = 'border-slate-300';
                bgClass = 'bg-slate-50/50';
                textClass = 'text-slate-600';
                etaClass = 'text-slate-500';
                statusLabel = <span className="text-xs text-slate-500">Skipped</span>;
                numberColor = 'text-slate-400';
              }
              
              return (
                <div
                  key={`stop-${stop.order}-${stop.status}`}
                  className={`bg-white rounded-lg border ${borderClass} ${bgClass} p-3`}
                >
                  <div className="flex items-start gap-2">
                    {/* Colored text number matching Route Map style */}
                    <div className={`w-6 h-6 flex items-center justify-center text-sm font-bold ${numberColor} flex-shrink-0`} style={{textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'}}>
                      {stop.order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${textClass}`}>
                        {stop.address}
                      </p>
                      {stop.notes && (
                        <p className="text-sm text-slate-500 mt-1">{stop.notes}</p>
                      )}
                      {stop.service_time_minutes && stop.service_time_minutes !== 5 && (
                        <span className="inline-flex items-center gap-1 mt-1 mr-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" />
                          {stop.service_time_minutes}m
                        </span>
                      )}
                      {/* Priority badges */}
                      {stop.priority === 'first' && (
                        <span className="inline-flex items-center mt-1 mr-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                          FIRST
                        </span>
                      )}
                      {stop.priority === 'last' && (
                        <span className="inline-flex items-center mt-1 mr-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                          LAST
                        </span>
                      )}
                      {/* Time constraint badges */}
                      {stop.time_constraint_type && stop.time_constraint_type !== 'none' && !isDone && (() => {
                        const to12Hour = (timeStr) => {
                          if (!timeStr) return '';
                          const parts = timeStr.split(':').map(Number);
                          const h = parts[0] || 0;
                          const m = parts[1] || 0;
                          const period = h >= 12 ? 'PM' : 'AM';
                          const h12 = h % 12 || 12;
                          return `${h12}:${m.toString().padStart(2, '0')}${period}`;
                        };
                        
                        let constraintText = '';
                        if (stop.time_constraint_type === 'arrive_before') {
                          constraintText = `BY ${to12Hour(stop.time_constraint_time)}`;
                        } else if (stop.time_constraint_type === 'arrive_after') {
                          constraintText = `AFTER ${to12Hour(stop.time_constraint_time)}`;
                        } else if (stop.time_constraint_type === 'arrive_between') {
                          constraintText = `${to12Hour(stop.time_window_start)}-${to12Hour(stop.time_window_end)}`;
                        }
                        
                        return constraintText ? (
                          <span className="inline-flex items-center mt-1 mr-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            {constraintText}
                          </span>
                        ) : null;
                      })()}
                      {/* On Time / Early / Late badges */}
                      {stop.time_constraint_type && stop.time_constraint_type !== 'none' && !isDone && stop.estimated_arrival && (() => {
                        const parseTime = (timeStr) => {
                          if (!timeStr) return null;
                          const parts = timeStr.split(':').map(Number);
                          return (parts[0] || 0) * 60 + (parts[1] || 0);
                        };
                        
                        const etaMinutes = parseTime(stop.estimated_arrival);
                        if (etaMinutes === null) return null;
                        
                        let status = null;
                        let diff = 0;
                        
                        if (stop.time_constraint_type === 'arrive_before') {
                          const targetEnd = parseTime(stop.time_constraint_time);
                          if (targetEnd !== null) {
                            diff = targetEnd - etaMinutes;
                            status = diff >= 0 ? 'ontime' : 'late';
                            diff = Math.abs(diff);
                          }
                        } else if (stop.time_constraint_type === 'arrive_after') {
                          const targetStart = parseTime(stop.time_constraint_time);
                          if (targetStart !== null) {
                            diff = etaMinutes - targetStart;
                            status = diff >= 0 ? 'ontime' : 'early';
                            diff = Math.abs(diff);
                          }
                        } else if (stop.time_constraint_type === 'arrive_between') {
                          const targetStart = parseTime(stop.time_window_start);
                          const targetEnd = parseTime(stop.time_window_end);
                          if (targetStart !== null && targetEnd !== null) {
                            if (etaMinutes < targetStart) {
                              status = 'early';
                              diff = targetStart - etaMinutes;
                            } else if (etaMinutes > targetEnd) {
                              status = 'late';
                              diff = etaMinutes - targetEnd;
                            } else {
                              status = 'ontime';
                            }
                          }
                        }
                        
                        if (!status) return null;
                        
                        if (status === 'ontime') {
                          return <span className="inline-flex items-center mt-1 mr-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">ON TIME</span>;
                        } else if (status === 'early') {
                          return <span className="inline-flex items-center mt-1 mr-1 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{diff}M EARLY</span>;
                        } else if (status === 'late') {
                          return <span className="inline-flex items-center mt-1 mr-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{diff}M LATE</span>;
                        }
                        return null;
                      })()}
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${etaClass}`}>
                        {isDone && stop.completed_at 
                          ? (() => {
                              const d = new Date(stop.completed_at);
                              const h = d.getHours();
                              const m = d.getMinutes();
                              return formatTime12h(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
                            })()
                          : formatTime12h(stop.estimated_arrival)
                        }
                      </p>
                      {statusLabel}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* End location - Red "E" text style */}
            {(route.return_to_start || route.end_address) && (
              <div className="bg-white rounded-lg border border-red-200 bg-red-50/30 p-3">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 flex items-center justify-center text-sm font-bold text-red-400 flex-shrink-0" style={{textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'}}>
                    E
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-red-700">
                      {route.return_to_start ? route.start_address : route.end_address}
                    </p>
                    <p className="text-xs text-red-500">End location</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Map View */
          <div className="mt-2 rounded-lg overflow-hidden shadow-lg border border-slate-200" style={{ height: 'calc(100vh - 220px)' }}>
            {mapBounds.length > 0 && (
              <MapContainer
                key={`map-${route.completed_stops}`}
                bounds={mapBounds}
                boundsOptions={{ padding: [30, 30] }}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                  tileSize={512}
                  zoomOffset={-1}
                />
                
                {/* Start marker */}
                {route.start_lat && route.start_lng && (
                  <Marker position={[route.start_lat, route.start_lng]} icon={startIcon}>
                    <Popup>
                      <strong>Start</strong><br />
                      {route.start_address}
                    </Popup>
                  </Marker>
                )}
                
                {/* End marker - only show if not returning to start and has separate end location */}
                {!route.return_to_start && route.end_lat && route.end_lng && (
                  <Marker position={[route.end_lat, route.end_lng]} icon={endIcon}>
                    <Popup>
                      <strong>End</strong><br />
                      {route.end_address || route.start_address}
                    </Popup>
                  </Marker>
                )}
                
                {/* Stop markers */}
                {route.stops.map((stop, index) => {
                  if (!stop.lat || !stop.lng) return null;
                  const stopIsDone = ['successful', 'completed', 'unsuccessful', 'skipped'].includes(stop.status);
                  const displayTime = stopIsDone && stop.completed_at
                    ? (() => {
                        const d = new Date(stop.completed_at);
                        const h = d.getHours();
                        const m = d.getMinutes();
                        return formatTime12h(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
                      })()
                    : formatTime12h(stop.estimated_arrival);
                  return (
                    <Marker
                      key={`marker-${stop.order}-${stop.status}`}
                      position={[stop.lat, stop.lng]}
                      icon={createNumberedIcon(stop.order, stop.status)}
                    >
                      <Popup>
                        <strong>Stop {stop.order}</strong><br />
                        {stop.address}<br />
                        <span className="text-slate-500">{stopIsDone ? 'Done' : 'ETA'}: {displayTime}</span>
                        {stop.status === 'successful' && <><br /><span className="text-green-600">✓ Served</span></>}
                        {stop.status === 'completed' && <><br /><span className="text-purple-600">✓ Complete</span></>}
                        {stop.status === 'unsuccessful' && <><br /><span className="text-red-600">✗ Failed</span></>}
                        {stop.status === 'skipped' && <><br /><span className="text-slate-500">○ Skipped</span></>}
                      </Popup>
                    </Marker>
                  );
                })}
                
                {/* Route line - use road geometry if available, otherwise straight lines */}
                {(() => {
                  // If we have cached road geometry from Mapbox, use it
                  if (route.route_geometry && route.route_geometry.coordinates && route.route_geometry.coordinates.length > 0) {
                    // GeoJSON coordinates are [lng, lat], Leaflet needs [lat, lng]
                    const roadCoords = route.route_geometry.coordinates.map(coord => [coord[1], coord[0]]);
                    return <Polyline positions={roadCoords} color="#0d9488" weight={4} opacity={0.8} />;
                  }
                  
                  // Fallback to straight lines between stops
                  const lineCoords = [];
                  if (route.start_lat && route.start_lng) {
                    lineCoords.push([route.start_lat, route.start_lng]);
                  }
                  route.stops.forEach(stop => {
                    if (stop.lat && stop.lng) {
                      lineCoords.push([stop.lat, stop.lng]);
                    }
                  });
                  if (route.return_to_start && route.start_lat && route.start_lng) {
                    lineCoords.push([route.start_lat, route.start_lng]);
                  } else if (route.end_lat && route.end_lng) {
                    lineCoords.push([route.end_lat, route.end_lng]);
                  }
                  return lineCoords.length > 1 ? (
                    <Polyline positions={lineCoords} color="#0d9488" weight={3} opacity={0.8} dashArray="5, 10" />
                  ) : null;
                })()}
              </MapContainer>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-3 text-center text-sm text-slate-400 z-40">
        Powered by Servio
      </footer>
    </div>
  );
}
